package com.karyadina.ui.screens.flashcards.components

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.karyadina.ui.theme.FlashcardColors
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.PI

/**
 * Circular Progress Graph - Shows overall mastery percentage
 * Animated from 0% to current percentage
 */
@Composable
fun CircularProgressGraph(
    percentage: Float,
    modifier: Modifier = Modifier,
    size: Float = 200f,
    strokeWidth: Float = 12f,
    animationDurationMillis: Int = 1500
) {
    val animatedProgress by animateFloatAsState(
        targetValue = percentage.coerceIn(0f, 100f) / 100f,
        animationSpec = tween(durationMillis = animationDurationMillis),
        label = "progressAnimation"
    )

    val progressColor = when {
        percentage >= 75f -> FlashcardColors.getCorrectAnswerColor()
        percentage >= 50f -> Color(0xFFFFC107) // Amber/Yellow
        else -> FlashcardColors.getIncorrectAnswerColor()
    }

    val backgroundColor = FlashcardColors.getStatsBackgroundColor()

    Column(
        modifier = modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Canvas(
            modifier = Modifier.size(size.dp)
        ) {
            val canvasSize = size.dp.toPx()
            val radius = canvasSize / 2 - strokeWidth
            val center = Offset(canvasSize / 2, canvasSize / 2)

            // Background circle
            drawArc(
                color = backgroundColor,
                startAngle = -90f,
                sweepAngle = 360f,
                useCenter = false,
                topLeft = Offset(
                    center.x - radius,
                    center.y - radius
                ),
                size = Size(radius * 2, radius * 2),
                style = Stroke(strokeWidth)
            )

            // Progress arc
            drawArc(
                color = progressColor,
                startAngle = -90f,
                sweepAngle = 360f * animatedProgress,
                useCenter = false,
                topLeft = Offset(
                    center.x - radius,
                    center.y - radius
                ),
                size = Size(radius * 2, radius * 2),
                style = Stroke(strokeWidth)
            )
        }

        // Percentage text
        Text(
            text = "${(animatedProgress * 100).toInt()}%",
            style = MaterialTheme.typography.displaySmall.copy(
                fontSize = 48.sp,
                fontWeight = FontWeight.Bold,
                color = progressColor
            )
        )

        Text(
            text = "Mastered",
            style = MaterialTheme.typography.titleMedium.copy(
                color = FlashcardColors.getQuestionTextColor().copy(alpha = 0.7f)
            )
        )
    }
}

/**
 * Horizontal Bar Chart - Shows subject comparison
 */
@Composable
fun BarChartGraph(
    items: List<BarChartItem>,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .padding(vertical = 16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        for (item in items) {
            BarChartItem(item = item)
        }
    }
}

@Composable
fun BarChartItem(item: BarChartItem) {
    var animationTrigger by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        animationTrigger = true
    }

    val animated by animateFloatAsState(
        targetValue = if (animationTrigger) item.percentage else 0f,
        animationSpec = tween(durationMillis = 800),
        label = "barAnimation"
    )

    val barColor = when {
        animated >= 75f -> FlashcardColors.getCorrectAnswerColor()
        animated >= 50f -> Color(0xFFFFC107)
        else -> FlashcardColors.getIncorrectAnswerColor()
    }

    val barBackgroundColor = FlashcardColors.getStatsBackgroundColor()

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 8.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = item.label,
                style = MaterialTheme.typography.labelMedium.copy(
                    fontWeight = FontWeight.SemiBold
                ),
                modifier = Modifier.weight(0.3f)
            )
            Text(
                text = "${animated.toInt()}%",
                style = MaterialTheme.typography.labelMedium.copy(
                    fontWeight = FontWeight.Bold,
                    color = barColor
                )
            )
        }

        Canvas(
            modifier = Modifier
                .fillMaxWidth()
                .height(12.dp)
        ) {
            val barWidth = size.width * (animated / 100f)
            val barHeight = size.height
            val cornerRadius = barHeight / 2

            // Background bar
            drawRoundRect(
                color = barBackgroundColor,
                size = Size(size.width, barHeight),
                cornerRadius = androidx.compose.ui.geometry.CornerRadius(cornerRadius)
            )

            // Progress bar
            drawRoundRect(
                color = barColor,
                size = Size(barWidth, barHeight),
                cornerRadius = androidx.compose.ui.geometry.CornerRadius(cornerRadius)
            )
        }
    }
}

data class BarChartItem(
    val label: String,
    val percentage: Float
)

/**
 * Line Graph - Shows progress trend over time
 */
@Composable
fun LineGraphChart(
    dataPoints: List<Float>,
    labels: List<String> = (0 until dataPoints.size).map { (it + 1).toString() },
    modifier: Modifier = Modifier,
    height: Float = 200f
) {
    if (dataPoints.isEmpty()) return

    var animationTrigger by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        animationTrigger = true
    }

    val animatedProgress by animateFloatAsState(
        targetValue = if (animationTrigger) 1f else 0f,
        animationSpec = tween(durationMillis = 1500),
        label = "lineAnimation"
    )

    // Get colors outside Canvas scope
    val gridColor = FlashcardColors.getStatsBackgroundColor()
    val lineColor = FlashcardColors.getMasteryColor("MASTERED")
    val labelTextColor = FlashcardColors.getQuestionTextColor()
    val correctColor = FlashcardColors.getCorrectAnswerColor()

    // Pre-calculate point colors before Canvas
    val pointColors = dataPoints.map { value ->
        when {
            value >= 75f -> correctColor
            value >= 50f -> Color(0xFFFFC107)
            value > 0f -> Color(0xFFFF9800)
            else -> gridColor.copy(alpha = 0.3f)
        }
    }

    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        // Scrollable graph for better detail on small screens
        val scrollState = androidx.compose.foundation.rememberScrollState()
        
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .horizontalScroll(scrollState)
        ) {
            Canvas(
                modifier = Modifier
                    .width((dataPoints.size * 50).dp.coerceAtLeast(300.dp))
                    .height(height.dp)
            ) {
                if (dataPoints.size < 2) return@Canvas

                val canvasHeight = size.height
                val canvasWidth = size.width
                val pointSpacing = canvasWidth / (dataPoints.size - 1)
                
                // Grid with Y-axis labels (0%, 25%, 50%, 75%, 100%)
                (0..4).forEach { i ->
                    val y = (canvasHeight / 4) * i
                    drawLine(
                        color = gridColor.copy(alpha = 0.2f),
                        start = Offset(0f, y),
                        end = Offset(canvasWidth, y),
                        strokeWidth = 1f
                    )
                }

                // Calculate all points (0-100% range)
                val points = dataPoints.mapIndexed { index, value ->
                    val x = pointSpacing * index
                    val normalizedValue = value.coerceIn(0f, 100f) / 100f
                    val y = canvasHeight - (normalizedValue * canvasHeight * animatedProgress)
                    Offset(x, y)
                }

                // Draw line
                (0 until points.size - 1).forEach { i ->
                    drawLine(
                        color = lineColor.copy(alpha = 0.8f),
                        start = points[i],
                        end = points[i + 1],
                        strokeWidth = 4f
                    )
                }

                // Draw colored points based on mastery level (using pre-calculated colors)
                for (index in points.indices) {
                    val point = points[index]
                    val value = dataPoints[index]
                    val pointColor = pointColors[index]
                    
                    drawCircle(color = pointColor, radius = 8f, center = point)
                    if (value > 0f) {
                        drawCircle(
                            color = pointColor.copy(alpha = 0.3f),
                            radius = 12f,
                            center = point,
                            style = Stroke(width = 2f)
                        )
                    }
                }
            }
        }

        // X-axis date labels
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            for (label in labels) {
                Column(modifier = Modifier.weight(1f), horizontalAlignment = Alignment.CenterHorizontally) {
                    Text(
                        text = label,
                        style = MaterialTheme.typography.labelSmall.copy(fontSize = 10.sp),
                        color = labelTextColor.copy(alpha = 0.7f)
                    )
                }
            }
        }
        
        // Show percentage values for each day
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            for (index in dataPoints.indices) {
                val value = dataPoints[index]
                Column(
                    modifier = Modifier.weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Text(
                        text = "${value.toInt()}%",
                        style = MaterialTheme.typography.labelMedium.copy(
                            fontWeight = FontWeight.Bold,
                            fontSize = 11.sp
                        ),
                        color = when {
                            value >= 75f -> FlashcardColors.getCorrectAnswerColor()
                            value >= 50f -> Color(0xFFFFC107)
                            value > 0f -> Color(0xFFFF9800)
                            else -> labelTextColor.copy(alpha = 0.3f)
                        }
                    )
                }
            }
        }
    }
}

/**
 * Stats Grid - Shows key statistics
 */
@Composable
fun StatsGridGraph(
    items: List<StatItem>,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        for (rowItems in items.chunked(2)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                for (item in rowItems) {
                    StatCard(
                        item = item,
                        modifier = Modifier
                            .weight(1f)
                            .height(120.dp)
                    )
                }
                // Add placeholder if odd number of items
                if (rowItems.size == 1) {
                    Spacer(modifier = Modifier.weight(1f))
                }
            }
        }
    }
}

@Composable
fun StatCard(
    item: StatItem,
    modifier: Modifier = Modifier
) {
    androidx.compose.material3.Surface(
        modifier = modifier,
        color = FlashcardColors.getStatsBackgroundColor().copy(alpha = 0.3f),
        shape = androidx.compose.foundation.shape.RoundedCornerShape(12.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(12.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Text(
                text = item.value,
                style = MaterialTheme.typography.headlineSmall.copy(
                    fontWeight = FontWeight.Bold,
                    color = item.color
                )
            )
            Spacer(modifier = Modifier.height(4.dp))
            Text(
                text = item.label,
                style = MaterialTheme.typography.labelMedium,
                color = FlashcardColors.getQuestionTextColor().copy(alpha = 0.6f)
            )
        }
    }
}

data class StatItem(
    val label: String,
    val value: String,
    val color: Color = Color(0xFF6200EE)
)
